import { useEffect, useState } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import Iconify from 'src/components/Iconify';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from 'src/@/components/ui/card';
import { Label } from 'src/@/components/ui/label';
import { Textarea } from 'src/@/components/ui/textarea';
import { calcularTokens } from 'src/utils/ai.utils';

const LABEL_CLS = 'text-xs font-semibold uppercase tracking-wider text-gray-500';
const INPUT_CLS = 'rounded-xl border-gray-200 focus:border-[#7C3AED] focus:ring-[#7C3AED]/20';

export default function InstrucoesAdicionaisSection({ aiAgent, changeModuleConfig }) {
    const { t } = useTranslation('settings');
    const complemento = aiAgent?.config?.module?.ecommerce?.complemento || {};
    const value = complemento.prompt || '';

    const [tokens, setTokens] = useState(0);
    useEffect(() => { setTokens(calcularTokens(value)); }, [value]);

    const updateComplemento = (patch) => {
        changeModuleConfig({ complemento: { ...complemento, ...patch } });
    };

    return (
        <Card className="rounded-xl border border-gray-100 bg-white">
            <CardHeader>
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-violet-50">
                        <Iconify icon="mdi:format-quote-close" width={20} className="text-violet-600" />
                    </div>
                    <div>
                        <CardTitle>{t('aiAgents.ecommerce.instrucoes.title', 'Instruções Adicionais')}</CardTitle>
                        <CardDescription>{t('aiAgents.ecommerce.instrucoes.subtitle', 'Regras específicas da sua operação que não cabem nas outras abas.')}</CardDescription>
                    </div>
                </div>
            </CardHeader>
            <CardContent className="flex flex-col gap-5">
                <div className="rounded-xl border border-amber-200 bg-amber-50/60 p-4 flex gap-3">
                    <Iconify icon="mdi:alert-circle-outline" width={18} className="text-amber-600 mt-0.5 shrink-0" />
                    <p className="text-xs text-amber-900/80 leading-relaxed">
                        {t('aiAgents.ecommerce.instrucoes.warning', 'Este texto vai direto para as instruções da assistente, junto com tudo que você configurou nas outras abas. Contradizer o que já está configurado ali gera comportamento imprevisível — prefira ajustar na aba certa.')}
                    </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="rounded-xl border border-emerald-100 bg-emerald-50/40 p-4">
                        <div className="flex items-center gap-2 mb-2">
                            <Iconify icon="mdi:check-circle-outline" width={16} className="text-emerald-600" />
                            <span className="text-xs font-semibold uppercase tracking-wider text-emerald-700">
                                {t('aiAgents.ecommerce.instrucoes.useTitle', 'Use para')}
                            </span>
                        </div>
                        <ul className="text-xs text-emerald-900/80 space-y-1.5 list-disc pl-5">
                            <li>{t('aiAgents.ecommerce.instrucoes.use1', 'Regras de negócio da loja ("a linha profissional é só por encomenda")')}</li>
                            <li>{t('aiAgents.ecommerce.instrucoes.use2', 'Jargão do seu setor e como o cliente costuma pedir os produtos')}</li>
                            <li>{t('aiAgents.ecommerce.instrucoes.use3', 'Situações em que ela deve encaminhar em vez de responder')}</li>
                        </ul>
                    </div>

                    <div className="rounded-xl border border-rose-100 bg-rose-50/40 p-4">
                        <div className="flex items-center gap-2 mb-2">
                            <Iconify icon="mdi:close-circle-outline" width={16} className="text-rose-600" />
                            <span className="text-xs font-semibold uppercase tracking-wider text-rose-700">
                                {t('aiAgents.ecommerce.instrucoes.avoidTitle', 'Não use para')}
                            </span>
                        </div>
                        <ul className="text-xs text-rose-900/80 space-y-1.5 list-disc pl-5">
                            <li>{t('aiAgents.ecommerce.instrucoes.avoid1', 'Preço e estoque — vêm do catálogo, sempre atualizados')}</li>
                            <li>{t('aiAgents.ecommerce.instrucoes.avoid2', 'Reescrever a persona ou o tom — use a aba Tom da Conversa')}</li>
                            <li>{t('aiAgents.ecommerce.instrucoes.avoid3', 'Regras de encaminhamento — use a aba Transferência')}</li>
                        </ul>
                    </div>
                </div>

                <div className="flex flex-col gap-2">
                    <Label className={LABEL_CLS}>{t('aiAgents.ecommerce.instrucoes.label', 'Instruções')}</Label>
                    <Textarea
                        value={value}
                        onChange={(e) => updateComplemento({ prompt: e.target.value })}
                        className={`${INPUT_CLS} font-mono text-sm leading-5`}
                        rows={12}
                        placeholder={t('aiAgents.ecommerce.instrucoes.placeholder', 'Ex.: quando o cliente perguntar sobre garantia estendida, não responda — encaminhe para o SAC.')}
                    />
                    <div className="flex justify-between items-center text-xs text-muted-foreground">
                        <span className="italic">
                            <Trans
                                t={t}
                                i18nKey="aiAgents.ecommerce.instrucoes.tip"
                                defaults="Frases curtas e diretas funcionam melhor que parágrafos."
                            />
                        </span>
                        <span>{t('aiAgents.ecommerce.instrucoes.tokens', 'Tokens aprox: {{tokens}}', { tokens })}</span>
                    </div>
                </div>
            </CardContent>
        </Card>
    );
}
